import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { WorkflowHistoryTimeline } from '../../components/WorkflowHistoryTimeline';
import { WorkflowRequestDetails } from '../../components/WorkflowRequestDetails';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  getWorkflowModuleCapabilities,
  WORKFLOW_INBOX_PERMISSIONS,
} from '../../config/workflowCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getWorkflowDetails,
  getWorkflowHistory,
} from '../../services/workflowService';
import type {
  WorkflowDetails,
  WorkflowHistoryItem,
} from '../../types/workflow';
import { formatAppliedOn } from '../../utils/dateTime';
import { formatWorkflowApprover } from '../../utils/hrAdminApprovalFields';
import { workflowErrorMessage } from '../../utils/workflowErrors';
import {
  getWorkflowApprovalRoute,
  getWorkflowRequestRoute,
} from '../../utils/workflowRequestType';
import { isRejectedWorkflowStatus, isCancelledWorkflowStatus } from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<RootStackParamList, 'WorkflowDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.label, { color: palette.textMuted }]}>{label}</Text>
      <Text style={[styles.value, { color: palette.textPrimary }]}>{value}</Text>
    </View>
  );
}

export function WorkflowDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getWorkflowModuleCapabilities(auth.permissions);
  const canAccess = auth.hasAnyPermission([...WORKFLOW_INBOX_PERMISSIONS]);
  const workflowInstanceId = route.params.workflowInstanceId;

  const [details, setDetails] = useState<WorkflowDetails | null>(null);
  const [history, setHistory] = useState<WorkflowHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const load = useCallback(async (isRefresh = false) => {
    if (!canAccess) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (!Number.isInteger(workflowInstanceId) || workflowInstanceId <= 0) {
      setDetails(null);
      setDetailsError('Approval request was not found.');
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    const detailsPromise = capabilities.canViewDetails
      ? getWorkflowDetails(workflowInstanceId)
      : Promise.reject(new Error('Details unavailable'));
    const historyPromise = capabilities.canViewHistory
      ? getWorkflowHistory(workflowInstanceId)
      : Promise.resolve([]);

    const [detailsResult, historyResult] = await Promise.allSettled([
      detailsPromise,
      historyPromise,
    ]);

    if (detailsResult.status === 'fulfilled') {
      setDetails(detailsResult.value);
      setDetailsError(null);
    } else {
      if (!isRefresh) setDetails(null);
      setDetailsError(
        workflowErrorMessage(
          detailsResult.reason,
          'Unable to load workflow details.',
        ),
      );
    }

    if (historyResult.status === 'fulfilled') {
      setHistory(historyResult.value);
      setHistoryError(null);
    } else {
      setHistory([]);
      setHistoryError(
        workflowErrorMessage(
          historyResult.reason,
          'Unable to load workflow history.',
        ),
      );
    }

    setLoading(false);
    setRefreshing(false);
  }, [
    canAccess,
    capabilities.canViewDetails,
    capabilities.canViewHistory,
    workflowInstanceId,
  ]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const openLinkedRequest = () => {
    if (!details) return;

    const approvalRoute = getWorkflowApprovalRoute(details.requestTypeCode);
    if (approvalRoute === 'leaveApproval' && auth.hasPermission(PERMISSIONS.LEAVE_APPROVE)) {
      navigation.navigate(routes.leaveApproval, {
        workflowInstanceId: details.workflowInstanceId,
      });
      return;
    }
    if (approvalRoute === 'wfhApproval' && auth.hasPermission(PERMISSIONS.WFH_APPROVE)) {
      navigation.navigate(routes.wfhApproval, {
        workflowInstanceId: details.workflowInstanceId,
      });
      return;
    }
    if (
      approvalRoute === 'timePermissionApproval' &&
      auth.hasPermission(PERMISSIONS.PERMISSION_APPROVE)
    ) {
      navigation.navigate(routes.timePermissionApproval, {
        workflowInstanceId: details.workflowInstanceId,
      });
      return;
    }

    if (
      approvalRoute === 'attendanceCorrectionApproval' &&
      auth.hasPermission(PERMISSIONS.ATTENDANCE_APPROVE)
    ) {
      navigation.navigate(routes.attendanceCorrectionApproval, {
        workflowInstanceId: details.workflowInstanceId,
      });
      return;
    }

    if (!details.requestReferenceId) return;
    const linked = getWorkflowRequestRoute(details.requestTypeCode);
    if (linked === 'wfhDetails' && auth.hasPermission(PERMISSIONS.WFH_APPLY)) {
      navigation.navigate(routes.wfhDetails, {
        wfhRequestId: details.requestReferenceId,
      });
      return;
    }
    if (
      linked === 'timePermissionDetails' &&
      auth.hasPermission(PERMISSIONS.TIME_PERMISSION_VIEW)
    ) {
      navigation.navigate(routes.timePermissionDetails, {
        permissionRequestId: details.requestReferenceId,
      });
      return;
    }
    if (
      linked === 'attendanceCorrectionDetails' &&
      auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW)
    ) {
      navigation.navigate(routes.attendanceCorrectionDetails, {
        correctionRequestId: details.requestReferenceId,
      });
      return;
    }
    if (linked === 'leaveDetails' && auth.hasPermission(PERMISSIONS.LEAVE_APPLY)) {
      navigation.navigate(routes.leaveDetails, {
        leaveApplicationId: details.requestReferenceId,
      });
    }
  };

  const approvalRoute = details
    ? getWorkflowApprovalRoute(details.requestTypeCode)
    : null;
  const canOpenModuleApproval =
    (approvalRoute === 'leaveApproval' &&
      auth.hasPermission(PERMISSIONS.LEAVE_APPROVE)) ||
    (approvalRoute === 'wfhApproval' &&
      auth.hasPermission(PERMISSIONS.WFH_APPROVE)) ||
    (approvalRoute === 'timePermissionApproval' &&
      auth.hasPermission(PERMISSIONS.PERMISSION_APPROVE)) ||
    (approvalRoute === 'attendanceCorrectionApproval' &&
      auth.hasPermission(PERMISSIONS.ATTENDANCE_APPROVE));

  const linkedRoute = details
    ? getWorkflowRequestRoute(details.requestTypeCode)
    : null;
  const canOpenEmployeeDetails =
    !!details?.requestReferenceId &&
    ((linkedRoute === 'leaveDetails' &&
      auth.hasPermission(PERMISSIONS.LEAVE_APPLY)) ||
      (linkedRoute === 'wfhDetails' &&
        auth.hasPermission(PERMISSIONS.WFH_APPLY)) ||
      (linkedRoute === 'timePermissionDetails' &&
        auth.hasPermission(PERMISSIONS.TIME_PERMISSION_VIEW)) ||
      (linkedRoute === 'attendanceCorrectionDetails' &&
        auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW)));
  const showLinkedRequestButton = canOpenModuleApproval || canOpenEmployeeDetails;

  const themed = useMemo(
    () => ({
      section: { ...typography.sectionTitle, color: palette.textPrimary },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginTop: spacing.md,
        marginBottom: spacing.sm,
      },
    }),
    [palette],
  );
  const approverLabel = details ? formatWorkflowApprover(details) : null;

  return (
    <PermissionGuard
      anyPermissions={WORKFLOW_INBOX_PERMISSIONS}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Approval Details"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading approval details" />
        </AppScreen>
      ) : detailsError && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Approval Details"
            onBack={navigation.goBack}
          />
          <ErrorState message={detailsError} onRetry={load} />
        </AppScreen>
      ) : details ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                load(true).catch(() => undefined);
              }}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader
            title="Approval Details"
            onBack={navigation.goBack}
          />

          <View style={styles.body}>

          <AppCard style={styles.card}>
            <Text style={themed.section}>Request summary</Text>
            <Field label="Request type" value={details.requestTypeLabel} palette={palette} />
            <Field
              label="Request number"
              value={
                details.requestNumber?.trim() ||
                `#${details.workflowInstanceId}`
              }
              palette={palette}
            />
            <Field
              label="Employee"
              value={
                details.employee.displayName ||
                details.employee.employeeNumber ||
                'Not available'
              }
              palette={palette}
            />
            {details.employee.employeeNumber ? (
              <Field
                label="Employee number"
                value={details.employee.employeeNumber}
                palette={palette}
              />
            ) : null}
            {details.employee.departmentName ? (
              <Field
                label="Department"
                value={details.employee.departmentName}
                palette={palette}
              />
            ) : null}
            {details.employee.designationName ? (
              <Field
                label="Designation"
                value={details.employee.designationName}
                palette={palette}
              />
            ) : null}
            <Field
              label="Submitted"
              value={formatAppliedOn(details.submittedAt)}
              palette={palette}
            />
            <Field label="Status" value={details.workflowStatusLabel} palette={palette} />
            {approverLabel ? (
              <Field
                label="Approver"
                value={approverLabel}
                palette={palette}
              />
            ) : null}
            {isRejectedWorkflowStatus(details.workflowStatusCode) ? (
              <Field
                label="Reject Reason"
                value={details.rejectReason?.trim() || 'Not available'}
                palette={palette}
              />
            ) : null}
            {isCancelledWorkflowStatus(details.workflowStatusCode) ? (
              <Field
                label="Cancellation Reason"
                value={details.cancellationReason?.trim() || 'Not available'}
                palette={palette}
              />
            ) : null}
            {details.currentStep?.stepName ? (
              <Field
                label="Current step"
                value={details.currentStep.stepName}
                palette={palette}
              />
            ) : null}
          </AppCard>

          <WorkflowRequestDetails
            requestTypeCode={details.requestTypeCode}
            requestDetails={details.requestDetails}
          />

          {showLinkedRequestButton ? (
            <AppButton
              title={
                canOpenModuleApproval
                  ? 'Review and approve'
                  : 'Open linked request'
              }
              variant={canOpenModuleApproval ? 'primary' : 'secondary'}
              onPress={openLinkedRequest}
            />
          ) : null}

          <WorkflowHistoryTimeline
            items={history}
            error={historyError}
            onRetry={load}
          />

          {!canOpenModuleApproval ? (
            <Text style={themed.warning}>
              Approve and reject for this request type are handled on the module
              approval screen when available.
            </Text>
          ) : null}
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: spacing.sm, marginBottom: spacing.md },
  field: { gap: 2 },
  label: { ...typography.caption },
  value: { ...typography.body },
});
